/**
 * Three-layer parallax starfield shader — twinkling points
 * with depth-based sizing and additive blending.
 *
 * Background and star colors are driven by theme tokens via uniforms:
 *   uBgDark      ← --globe-bg-dark     (outer edge of radial gradient)
 *   uBgMid       ← --globe-bg-mid      (center glow color)
 *   uStarColor   ← --globe-star-color  (star tint)
 *
 * These uniforms are set in DiscoverGlobe.tsx from the active theme tokens.
 * The Theme Designer (Admin → Theme Designer → Globe / Discover) lets admins
 * customize these per theme.
 */

export const starVertexShader = /* glsl */ `
  attribute float size;
  varying float vBrightness;
  uniform float time;
  void main() {
    float twinkle = sin(time * 0.8 + position.x * 5.0 + position.z * 3.0) * 0.15 + 0.85;
    vBrightness = twinkle;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (180.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

export const starFragmentShader = /* glsl */ `
  uniform vec3 uStarColor;
  varying float vBrightness;
  void main() {
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    float core = 1.0 - smoothstep(0.0, 0.15, d);
    float soft = (1.0 - smoothstep(0.0, 0.5, d)) * 0.3;
    float alpha = (core + soft) * vBrightness;
    gl_FragColor = vec4(uStarColor, alpha);
  }
`;

/** Connection beam shader — pulsing arc along tube UVs. */
export const beamVertexShader = /* glsl */ `
  varying float vT;
  void main() {
    vT = uv.x;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const beamFragmentShader = /* glsl */ `
  uniform float time;
  uniform vec3 color;
  uniform float offset;
  varying float vT;
  void main() {
    float pulse = fract(vT - time * 0.5 + offset);
    pulse = smoothstep(0.0, 0.15, pulse) * smoothstep(0.4, 0.25, pulse);
    float fade = sin(vT * 3.14159);
    gl_FragColor = vec4(color, pulse * fade * 0.5);
  }
`;

/** Background gradient shader — colors driven by theme uniforms. */
export const bgVertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.99, 1.0);
  }
`;

export const bgFragmentShader = /* glsl */ `
  uniform vec3 uBgDark;
  uniform vec3 uBgMid;
  varying vec2 vUv;
  void main() {
    vec2 center = vec2(0.5, 0.45);
    float d = distance(vUv, center);
    vec3 color = mix(uBgMid, uBgDark, smoothstep(0.0, 0.7, d));
    gl_FragColor = vec4(color, 1.0);
  }
`;
